import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import ConfirmDialog from '../ui/ConfirmDialog';

const OBJECT_TYPE_LABELS = {
  plant: 'Pflanze',
  rectangle: 'Rechteck',
  circle: 'Kreis',
  line: 'Linie',
  polygon: 'Polygon',
  bezier: 'Kurve',
  path: 'Weg',
};

/**
 * PropertiesDrawerSheet — Mobile-only bottom sheet replacing the desktop PropertiesPanel.
 *
 * 3 states:
 *  - Empty (no selection): drag handle + hint text
 *  - Single object: drag handle + fill color + gray delete button
 *  - Multi-select: drag handle + fill color + "N Objekte löschen" button
 *
 * Positioned above the BottomTabBar (bottom: 68px).
 * Max height 62vh when expanded.
 */
const PropertiesDrawerSheet = ({ selectedObject, onDelete, onUpdate, canvas }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [fillColor, setFillColor] = useState('#cccccc');
  // Seeded to null (not selectedObject) so an object already selected on mount
  // still triggers the auto-expand below on the first render.
  const [prevSelected, setPrevSelected] = useState(null);

  const isMultiSelection = selectedObject?.type === 'activeselection';
  const multiCount = isMultiSelection ? (selectedObject.getObjects?.()?.length ?? 0) : 0;

  // Auto-expand when an object is selected; collapse when deselected. Adjusted
  // during render rather than in an effect to avoid a setState-in-effect cascade.
  if (selectedObject !== prevSelected) {
    setPrevSelected(selectedObject);
    if (!selectedObject) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
      const fill = selectedObject.fill;
      setFillColor(
        fill && typeof fill === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(fill)
          ? fill
          : '#cccccc'
      );
    }
  }

  const getObjectLabel = () => {
    if (!selectedObject) return '';
    if (isMultiSelection) return `${multiCount} Objekte ausgewählt`;
    const typeId =
      selectedObject.data?.gardenObjectType ||
      selectedObject.data?.objectTypeId ||
      selectedObject.objectType ||
      '';
    return OBJECT_TYPE_LABELS[typeId] || 'Objekt';
  };

  const handleFillChange = (color) => {
    setFillColor(color);
    if (!selectedObject) return;
    if (isMultiSelection) {
      selectedObject.getObjects?.().forEach((obj) => obj.set('fill', color));
    } else {
      selectedObject.set('fill', color);
    }
    canvas?.requestRenderAll();
    onUpdate?.();
  };

  const handleConfirmDelete = () => {
    setShowDeleteDialog(false);
    setIsExpanded(false);
    onDelete?.(selectedObject);
  };

  return (
    <>
      <div
        className="fixed left-0 right-0 bg-white border-t border-gray-200 rounded-t-xl overflow-hidden"
        style={{
          bottom: 68,
          zIndex: 50,
          maxHeight: isExpanded ? '62vh' : 48,
          transition: 'max-height 250ms ease-out',
        }}
        data-testid="properties-drawer-sheet"
      >
        {/* Drag handle — toggles expand/collapse */}
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          className="w-full flex flex-col items-center justify-center gap-1"
          style={{ height: 48, flexShrink: 0 }}
          aria-label={isExpanded ? 'Eigenschaften schließen' : 'Eigenschaften öffnen'}
          aria-expanded={isExpanded}
        >
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
          {!selectedObject && !isExpanded && (
            <span className="text-[11px] text-gray-400">Tippe auf ein Objekt</span>
          )}
        </button>

        {/* Sheet content — only rendered when expanded */}
        {isExpanded && (
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(62vh - 48px)' }}>
            {!selectedObject ? (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-gray-500">
                  Tippe auf ein Objekt, um Details anzuzeigen.
                </p>
              </div>
            ) : (
              <>
                {/* Object type header */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{getObjectLabel()}</p>
                </div>

                {/* Simplified properties */}
                <div className="px-4 py-4 space-y-4">
                  <div>
                    <label htmlFor="drawer-fill-color" className="block text-xs font-medium text-gray-700 mb-1.5">
                      Füllung
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        id="drawer-fill-color"
                        type="color"
                        value={fillColor}
                        onChange={(e) => handleFillChange(e.target.value)}
                        className="w-8 h-8 rounded border border-gray-300 cursor-pointer p-0.5"
                        aria-label="Füllfarbe"
                      />
                      <span className="text-sm text-gray-600 font-mono text-[12px]">
                        {fillColor}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer: delete button — gray per wireframe spec */}
                <div className="px-4 py-3 border-t border-gray-100">
                  <button
                    onClick={() => setShowDeleteDialog(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2
                      bg-gray-50 border border-gray-300 text-gray-700 rounded
                      hover:bg-gray-100 transition-colors font-medium text-sm"
                    aria-label={
                      isMultiSelection ? `${multiCount} Objekte löschen` : 'Objekt löschen'
                    }
                  >
                    <Trash2 size={16} />
                    {isMultiSelection ? `${multiCount} Objekte löschen` : 'Objekt löschen'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
        title="Objekt löschen"
        message="Möchtest du dieses Objekt wirklich löschen?"
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </>
  );
};

export default PropertiesDrawerSheet;
